import {
  AssistantChatAction,
  AssistantChatBlock,
  AssistantChatChartKind,
  AssistantChatChartSeries,
  AssistantChatHighlight,
  AssistantChatPoint,
  AssistantChatReportLink,
} from '../../interfaces/assistant-chat.interface';

export function wellFormedBlocks(blocks: unknown): AssistantChatBlock[] {
  return listOf(blocks)
    .filter(block => typeof block?.type === 'string')
    .map(block => wellFormed(block));
}

function wellFormed(block: any): AssistantChatBlock {
  const shaped = withTitleIfText(block);
  switch (block.type) {
    case 'text':
    case 'draft':
      return { ...shaped, body: textOf(block.body) };
    case 'table':
      return {
        ...shaped,
        columns: listOf(block.columns).map(textOf),
        rows: listOf(block.rows).map(row => listOf(row).map(textOf)),
      };
    case 'points':
      return { ...shaped, items: listOf(block.items).map(pointOf) };
    case 'highlights':
      return { ...shaped, items: listOf(block.items).map(highlightOf) };
    case 'working':
    case 'followups':
      return { ...shaped, items: listOf(block.items).map(textOf).filter(item => !!item) };
    case 'actions':
      return { ...shaped, items: listOf(block.items).map(actionOf).filter((action): action is AssistantChatAction => !!action) };
    case 'chart':
      return chartOf(shaped, block);
    default:
      return shaped;
  }
}

const CHART_KINDS: AssistantChatChartKind[] = ['bar', 'line', 'pie'];

function chartOf(shaped: any, block: any): AssistantChatBlock {
  const categories = listOf(block.categories).map(textOf);
  const series = listOf(block.series)
    .map(line => seriesOf(line, categories.length))
    .filter((line): line is AssistantChatChartSeries => !!line);
  if (!CHART_KINDS.includes(block.kind) || !categories.length || !series.length) {
    return { ...shaped, type: 'unreadable_chart' };
  }
  return { ...shaped, kind: block.kind, categories, series: block.kind === 'pie' ? series.slice(0, 1) : series };
}

function seriesOf(line: any, length: number): AssistantChatChartSeries | undefined {
  const values = listOf(line?.values).slice(0, length);
  const name = textOf(line?.name);
  if (!name || values.length < length || !values.every(value => typeof value === 'number' && isFinite(value))) {
    return undefined;
  }
  return { name, values };
}

function pointOf(point: any): AssistantChatPoint {
  return { ...withTitleIfText(point), body: textOf(point?.body) };
}

function highlightOf(item: any): AssistantChatHighlight {
  const highlight: AssistantChatHighlight = { label: textOf(item?.label), value: textOf(item?.value) };
  return item?.context === undefined ? highlight : { ...highlight, context: textOf(item.context) };
}

const INTERVAL = /^\d{8}-\d{8}$/;

function isReportLink(link: any): link is AssistantChatReportLink {
  const strings = (values: unknown) => Array.isArray(values) && values.every(value => typeof value === 'string');
  return typeof link?.dataset === 'string' && typeof link.selection === 'string' && INTERVAL.test(link.interval)
    && strings(link.dimensions) && strings(link.metrics) && (link.rollup === undefined || typeof link.rollup === 'string')
    && (link.properties === undefined || (strings(link.properties) && link.properties.every(named => /^[^.]+\..+$/.test(named))));
}

function actionOf(action: any): AssistantChatAction | undefined {
  if (typeof action?.id !== 'string' || typeof action?.label !== 'string') {
    return undefined;
  }
  const { report, studio, ...kept } = action;
  if (report !== undefined) {
    if (!isReportLink(report)) {
      return undefined;
    }
    kept.report = report;
  }
  if (studio !== undefined) {
    const links = listOf(studio).filter(isReportLink);
    if (!links.length) {
      return undefined;
    }
    kept.studio = links;
  }
  return kept;
}

function withTitleIfText(value: any): any {
  const { title, ...rest } = value || {};
  return typeof title === 'string' ? { ...rest, title } : rest;
}

function listOf(value: unknown): any[] {
  return Array.isArray(value) ? value : [];
}

function textOf(value: unknown): string {
  return typeof value === 'string' || typeof value === 'number' ? String(value) : '';
}
